import React, { useState, useEffect } from "react";
import {
  X,
  Plus,
  Trash2,
  Edit2,
  Check,
  MessageSquare,
  Calendar,
  Infinity,
} from "lucide-react";
import apiService from "../services/api";
import "../styles/EmployeeModal.css";

const EmployeeModal = ({ employee, onClose }) => {
  const [formData, setFormData] = useState({
    employee_name: "",
    employee_number: "",
    customer_id: "",
    type: "קבלן",
    payment_method: "קבלן",
    contractor: "",
    hour_rate: "",
    weekend_hour_rate: "",
    michpal_id: "",
    phone_number: "---",
    address: "---",
    active: true,
    // משכורת גלובלית - Global/fixed salary
    is_global_salary: false,
    global_salary_amount: "",
  });

  const [customers, setCustomers] = useState([]);
  const [selectedCustomerName, setSelectedCustomerName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Multi-site support: selected customers (up to 2)
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [secondCustomerName, setSecondCustomerName] = useState("");
  const [secondCustomerId, setSecondCustomerId] = useState("");

  // Notes state
  const [notes, setNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  const [newNoteType, setNewNoteType] = useState("permanent");
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState("");

  // Get unique customer names (API returns 'name' field)
  const uniqueCustomerNames = [...new Set(customers.map((c) => c.name))]
    .filter(Boolean)
    .sort();

  // Get sites for selected customer
  const sitesForCustomer = customers.filter(
    (c) => c.name === selectedCustomerName,
  );

  useEffect(() => {
    loadCustomers();
    if (employee) {
      loadNotes();
    }
  }, []);

  // Load notes for employee
  const loadNotes = async () => {
    if (!employee) return;
    try {
      setLoadingNotes(true);
      const response = await apiService.getEmployeeNotes(
        employee.employee_id,
        true,
      );
      setNotes(response.notes || []);
    } catch (error) {
      console.error("Failed to load notes:", error);
    } finally {
      setLoadingNotes(false);
    }
  };

  // Add new note
  const handleAddNote = async () => {
    if (!newNoteText.trim() || !employee) return;
    try {
      const noteData = { note_text: newNoteText, note_type: newNoteType };
      if (newNoteType === "monthly") {
        const now = new Date();
        noteData.month = now.getMonth() + 1;
        noteData.year = now.getFullYear();
      }
      await apiService.createEmployeeNote(employee.employee_id, noteData);
      setNewNoteText("");
      loadNotes();
    } catch (error) {
      console.error("Failed to add note:", error);
      alert("שגיאה בהוספת הערה");
    }
  };

  // Delete note
  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("האם אתה בטוח שברצונך למחוק הערה זו?")) return;
    try {
      await apiService.deleteNote(noteId);
      loadNotes();
    } catch (error) {
      console.error("Failed to delete note:", error);
      alert("שגיאה במחיקת הערה");
    }
  };

  // Start editing note
  const handleEditNote = (note) => {
    setEditingNoteId(note.note_id);
    setEditingNoteText(note.note_text);
  };

  // Save edited note
  const handleSaveNote = async (noteId) => {
    if (!editingNoteText.trim()) return;
    try {
      await apiService.updateNote(noteId, { note_text: editingNoteText });
      setEditingNoteId(null);
      setEditingNoteText("");
      loadNotes();
    } catch (error) {
      console.error("Failed to update note:", error);
      alert("שגיאה בעדכון הערה");
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditingNoteText("");
  };

  useEffect(() => {
    if (employee && customers.length > 0) {
      const employeeType = employee.type || "חברה";
      const defaultPaymentMethod =
        employeeType === "קבלן"
          ? "קבלן"
          : employee.payment_method || "העברה בנקאית";

      // Find the customer to set the customer name (API returns 'id' and 'name')
      const employeeCustomer = customers.find(
        (c) => c.id === employee.customer_id,
      );
      if (employeeCustomer) {
        setSelectedCustomerName(employeeCustomer.name);
      }

      // Handle multiple customers (multi-site support)
      if (employee.customers && employee.customers.length > 0) {
        setSelectedCustomers(employee.customers);
        // Set first customer
        const firstCustomer = employee.customers[0];
        if (firstCustomer) {
          const cust = customers.find(
            (c) => c.id === firstCustomer.customer_id,
          );
          if (cust) {
            setSelectedCustomerName(cust.name);
          }
        }
        // Set second customer if exists
        if (employee.customers.length > 1) {
          const secondCustomer = employee.customers[1];
          const cust = customers.find(
            (c) => c.id === secondCustomer.customer_id,
          );
          if (cust) {
            setSecondCustomerName(cust.name);
            setSecondCustomerId(secondCustomer.customer_id);
          }
        }
      }

      setFormData({
        employee_name: employee.employee_name || "",
        employee_number: employee.employee_number || "",
        customer_id: employee.customer_id || "",
        type: employeeType,
        payment_method: defaultPaymentMethod,
        contractor: employee.contractor || "",
        hour_rate: employee.hour_rate || "",
        weekend_hour_rate: employee.weekend_hour_rate || "",
        michpal_id: employee.michpal_id || "",
        phone_number: employee.phone_number || "---",
        address: employee.address || "---",
        active: employee.active !== undefined ? employee.active : true,
        // משכורת גלובלית - Global/fixed salary
        is_global_salary: employee.is_global_salary || false,
        global_salary_amount: employee.global_salary_amount || "",
      });
    }
  }, [employee, customers]);

  const loadCustomers = async () => {
    try {
      const response = await apiService.getCustomers({ limit: 1000 });
      setCustomers(response.customers || []);
    } catch (error) {
      console.error("Failed to load customers:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: newValue,
      };

      // If employee type changes to קבלן, set payment method to קבלן
      if (name === "type" && value === "קבלן") {
        updated.payment_method = "קבלן";
      }
      // If employee type changes to חברה or includes חברה, default to העברה בנקאית
      else if (
        name === "type" &&
        (value === "חברה" || value.includes("חברה"))
      ) {
        updated.payment_method = "העברה בנקאית";
      }

      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Prepare data - convert empty strings to null
      const dataToSend = {};
      Object.keys(formData).forEach((key) => {
        const value = formData[key];
        if (value === "") {
          dataToSend[key] = null;
        } else if (key === "customer_id" || key === "michpal_id") {
          dataToSend[key] = value ? parseInt(value) : null;
        } else if (
          key === "hour_rate" ||
          key === "weekend_hour_rate" ||
          key === "global_salary_amount"
        ) {
          dataToSend[key] = value ? parseFloat(value) : null;
        } else if (key === "is_global_salary") {
          dataToSend[key] = value === true;
        } else {
          dataToSend[key] = value;
        }
      });

      // Ensure payment_method is "קבלן" when type is "קבלן"
      if (dataToSend.type === "קבלן") {
        dataToSend.payment_method = "קבלן";
      }

      if (employee) {
        // Update existing employee
        await apiService.updateEmployee(employee.employee_id, dataToSend);

        // Update customer assignments (multi-site support)
        const customerIds = [];
        if (dataToSend.customer_id) {
          customerIds.push(dataToSend.customer_id);
        }
        if (secondCustomerId && secondCustomerId !== dataToSend.customer_id) {
          customerIds.push(parseInt(secondCustomerId));
        }
        if (customerIds.length > 0) {
          await apiService.updateEmployeeCustomers(
            employee.employee_id,
            customerIds,
            dataToSend.customer_id,
          );
        }

        alert("עובד עודכן בהצלחה");
      } else {
        // Create new employee
        const result = await apiService.createEmployee(dataToSend);

        // Add second customer if selected (multi-site support)
        if (result.success && result.employee && secondCustomerId) {
          await apiService.addEmployeeCustomer(
            result.employee.employee_id,
            parseInt(secondCustomerId),
          );
        }

        alert("עובד נוצר בהצלחה");
      }
      onClose(true); // true indicates reload is needed
    } catch (error) {
      console.error("Failed to save employee:", error);
      setError(error.response?.data?.error || "שגיאה בשמירת עובד");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='modal-overlay' onClick={() => onClose(false)}>
      <div className='modal-content' onClick={(e) => e.stopPropagation()}>
        <div className='modal-header'>
          <h2>{employee ? "ערוך עובד" : "הוסף עובד חדש"}</h2>
          <button className='modal-close' onClick={() => onClose(false)}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className='modal-form'>
          {error && <div className='error-message'>❌ {error}</div>}

          <div className='form-grid'>
            <div className='form-field'>
              <label>
                שם עובד <span className='required'>*</span>
              </label>
              <input
                type='text'
                name='employee_name'
                value={formData.employee_name}
                onChange={handleChange}
                required
                placeholder='הזן שם עובד'
              />
            </div>

            <div className='form-field'>
              <label>מספר עובד</label>
              <input
                type='text'
                name='employee_number'
                value={formData.employee_number}
                onChange={handleChange}
                placeholder='הזן מספר עובד'
              />
            </div>

            <div className='form-field'>
              <label>לקוח</label>
              <select
                value={selectedCustomerName}
                onChange={(e) => {
                  setSelectedCustomerName(e.target.value);
                  setFormData((prev) => ({ ...prev, customer_id: "" }));
                }}
              >
                <option value=''>בחר לקוח...</option>
                {uniqueCustomerNames.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div className='form-field'>
              <label>אתר ראשי</label>
              <select
                name='customer_id'
                value={formData.customer_id}
                onChange={handleChange}
                disabled={!selectedCustomerName}
              >
                <option value=''>בחר אתר...</option>
                {sitesForCustomer.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.site_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Second Customer/Site - Multi-site support */}
            <div className='form-field'>
              <label>לקוח נוסף (אופציונלי)</label>
              <select
                value={secondCustomerName}
                onChange={(e) => {
                  setSecondCustomerName(e.target.value);
                  setSecondCustomerId("");
                }}
              >
                <option value=''>בחר לקוח נוסף...</option>
                {uniqueCustomerNames
                  .filter((name) => name !== selectedCustomerName)
                  .map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
              </select>
            </div>

            <div className='form-field'>
              <label>אתר נוסף</label>
              <select
                value={secondCustomerId}
                onChange={(e) => setSecondCustomerId(e.target.value)}
                disabled={!secondCustomerName}
              >
                <option value=''>בחר אתר נוסף...</option>
                {customers
                  .filter(
                    (c) =>
                      c.name === secondCustomerName &&
                      c.id !== parseInt(formData.customer_id),
                  )
                  .map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.site_name}
                    </option>
                  ))}
              </select>
            </div>

            <div className='form-field'>
              <label>
                סוג עובד <span className='required'>*</span>
              </label>
              <select
                name='type'
                value={formData.type}
                onChange={handleChange}
                required
              >
                <option value='קבלן'>קבלן</option>
                <option value='חברה'>חברה</option>
                <option value='קבלן ו חברה'>קבלן ו חברה</option>
                <option value='חברה וקבלן'>חברה וקבלן</option>
              </select>
            </div>

            {(formData.type === "חברה" ||
              formData.type === "קבלן ו חברה" ||
              formData.type === "חברה וקבלן") && (
              <div className='form-field'>
                <label>
                  אמצעי תשלום <span className='required'>*</span>
                </label>
                <select
                  name='payment_method'
                  value={formData.payment_method}
                  onChange={handleChange}
                  required
                >
                  <option value='צ׳ק'>צ׳ק</option>
                  <option value='העברה בנקאית'>העברה בנקאית</option>
                </select>
              </div>
            )}

            <div className='form-field'>
              <label>קבלן</label>
              <select
                name='contractor'
                value={formData.contractor}
                onChange={handleChange}
              >
                <option value=''>ללא קבלן</option>
                <option value='מוניר'>מוניר</option>
                <option value='איברהים'>איברהים</option>
                <option value='איתן שמיר'>איתן שמיר</option>
                <option value='ואי גי אס'>ואי גי אס</option>
              </select>
            </div>

            <div className='form-field'>
              <label>תעריף שעתי (₪)</label>
              <input
                type='number'
                name='hour_rate'
                value={formData.hour_rate}
                onChange={handleChange}
                step='0.01'
                min='0'
                placeholder='0.00'
              />
            </div>

            <div className='form-field'>
              <label>תעריף שעתי סופ"ש (₪)</label>
              <input
                type='number'
                name='weekend_hour_rate'
                value={formData.weekend_hour_rate}
                onChange={handleChange}
                step='0.01'
                min='0'
                placeholder='0.00'
              />
            </div>

            {/* משכורת גלובלית - Global/fixed salary section */}
            <div className='form-field checkbox-field global-salary-field'>
              <label>
                <input
                  type='checkbox'
                  name='is_global_salary'
                  checked={formData.is_global_salary}
                  onChange={handleChange}
                />
                <span>משכורת גלובלית (קבועה)</span>
              </label>
              <small className='field-hint'>
                כאשר מופעל, המשכורת תהיה קבועה ולא תחושב לפי שעות
              </small>
            </div>

            {formData.is_global_salary && (
              <div className='form-field global-salary-amount'>
                <label>
                  סכום משכורת גלובלית (₪) <span className='required'>*</span>
                </label>
                <input
                  type='number'
                  name='global_salary_amount'
                  value={formData.global_salary_amount}
                  onChange={handleChange}
                  step='0.01'
                  min='0'
                  placeholder='0.00'
                  required={formData.is_global_salary}
                />
              </div>
            )}

            <div className='form-field'>
              <label>מזהה מיכפל</label>
              <input
                type='number'
                name='michpal_id'
                value={formData.michpal_id}
                onChange={handleChange}
                placeholder='הזן מזהה מיכפל'
              />
            </div>

            <div className='form-field'>
              <label>טלפון</label>
              <input
                type='tel'
                name='phone_number'
                value={formData.phone_number}
                onChange={handleChange}
                placeholder='הזן מספר טלפון'
              />
            </div>

            <div className='form-field'>
              <label>כתובת</label>
              <input
                type='text'
                name='address'
                value={formData.address}
                onChange={handleChange}
                placeholder='הזן כתובת'
              />
            </div>

            <div className='form-field checkbox-field'>
              <label>
                <input
                  type='checkbox'
                  name='active'
                  checked={formData.active}
                  onChange={handleChange}
                />
                <span>עובד פעיל</span>
              </label>
            </div>
          </div>

          {/* Notes Section - Only show when editing existing employee */}
          {employee && (
            <div className='notes-section'>
              <div className='notes-header'>
                <h3>
                  <MessageSquare size={18} />
                  הערות לעובד ({notes.length})
                </h3>
              </div>

              {/* Add new note */}
              <div className='add-note-form'>
                <div className='note-type-selector'>
                  <button
                    type='button'
                    className={`note-type-btn ${newNoteType === "monthly" ? "active" : ""}`}
                    onClick={() => setNewNoteType("monthly")}
                  >
                    <Calendar size={14} />
                    חודשי
                  </button>
                  <button
                    type='button'
                    className={`note-type-btn ${newNoteType === "permanent" ? "active" : ""}`}
                    onClick={() => setNewNoteType("permanent")}
                  >
                    <Infinity size={14} />
                    קבוע
                  </button>
                </div>
                <div className='add-note-input-row'>
                  <input
                    type='text'
                    value={newNoteText}
                    onChange={(e) => setNewNoteText(e.target.value)}
                    placeholder={
                      newNoteType === "monthly"
                        ? "הוסף הערה חודשית..."
                        : "הוסף הערה קבועה..."
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleAddNote();
                      }
                    }}
                  />
                  <button
                    type='button'
                    onClick={handleAddNote}
                    disabled={!newNoteText.trim()}
                    className='btn-add-note'
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>

              {/* Notes list */}
              <div className='notes-list'>
                {loadingNotes ? (
                  <div className='notes-loading'>טוען הערות...</div>
                ) : notes.length === 0 ? (
                  <div className='notes-empty'>אין הערות לעובד זה</div>
                ) : (
                  notes.map((note) => (
                    <div key={note.note_id} className='note-item'>
                      {editingNoteId === note.note_id ? (
                        <div className='note-edit-form'>
                          <input
                            type='text'
                            value={editingNoteText}
                            onChange={(e) => setEditingNoteText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleSaveNote(note.note_id);
                              } else if (e.key === "Escape") {
                                handleCancelEdit();
                              }
                            }}
                            autoFocus
                          />
                          <div className='note-edit-actions'>
                            <button
                              type='button'
                              onClick={() => handleSaveNote(note.note_id)}
                              className='btn-save-note'
                            >
                              <Check size={14} />
                            </button>
                            <button
                              type='button'
                              onClick={handleCancelEdit}
                              className='btn-cancel-note'
                            >
                              <X size={14} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className='note-content'>
                            <span
                              className={`note-type-badge ${note.note_type}`}
                            >
                              {note.note_type === "permanent"
                                ? "קבוע"
                                : "חודשי"}
                            </span>
                            <span className='note-text'>{note.note_text}</span>
                          </div>
                          <div className='note-actions'>
                            <button
                              type='button'
                              onClick={() => handleEditNote(note)}
                              className='btn-edit-note'
                              title='ערוך'
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              type='button'
                              onClick={() => handleDeleteNote(note.note_id)}
                              className='btn-delete-note'
                              title='מחק'
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          <div className='modal-footer'>
            <button
              type='button'
              className='btn-secondary'
              onClick={() => onClose(false)}
              disabled={loading}
            >
              ביטול
            </button>
            <button type='submit' className='btn-primary' disabled={loading}>
              {loading ? "שומר..." : employee ? "עדכן עובד" : "צור עובד"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeeModal;
